import {ChangeEvent, useCallback, useState} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {saveEventThunk} from '~/CreateFabricInvitation/api/event';
import {OverlayContentWrapper} from '~/CreateFabricInvitation/routes/EventGifting/components/OverlayContentWrapper/OverlayContentWrapper';
import {VendorImages} from '~/CreateFabricInvitation/routes/EventGifting/components/VendorImages/VendorImages';
import {
  formatUrlWithProtocol,
  validURL,
} from '~/common/fabric/CreateDesign/components/EventGifting/utils';
import {UPDATE_GIFT_OPTIONS} from '~/common/fabric/reducers/giftingOptions/constants';
import {
  selectRegistryNote,
  selectRegistryRecipient,
  selectRegistryUrls,
} from '~/common/fabric/reducers/giftingOptions/selectors';
import {Input} from '~library/atoms/Input';
import {ResponsiveModal} from '~library/organisms/ResponsiveModal';
import {scrollElementIntoViewById} from '~library/utils/misc';
import {RegistryFooter} from './RegistryFooter';
import {RegistryList} from './RegistryList';

import './RegistryOverlayContent.sass';

type RegistryOverlayContentProps = {
  onClose: () => void;
};

export const RegistryOverlayContent = ({onClose}: RegistryOverlayContentProps) => {
  const dispatch = useDispatch();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const regUrls = useSelector(selectRegistryUrls, shallowEqual);
  const [localUrls, setLocalUrls] = useState<string[]>(regUrls);
  const giftRegistryName = useSelector(selectRegistryRecipient);
  const giftRegistryNote = useSelector(selectRegistryNote);

  const onSaveRegistriesHandler = useCallback(async () => {
    if (localUrls.length === 0) return;

    const normalizedUrls = localUrls
      .map((url) => {
        const formattedRegistryUrl = formatUrlWithProtocol(url);
        return new URL(formattedRegistryUrl).toString() || formattedRegistryUrl;
      })
      .filter((registryUrl) => validURL(registryUrl));

    dispatch({
      type: UPDATE_GIFT_OPTIONS,
      payload: {
        // biome-ignore lint/style/useNamingConvention: API expects the property name in snake_case
        gift_registry_urls: normalizedUrls,
        saving: true,
      },
    });

    onClose();

    // @ts-expect-error function not typed
    await dispatch(saveEventThunk());

    dispatch({
      type: UPDATE_GIFT_OPTIONS,
      payload: {
        saving: false,
      },
    });
  }, [dispatch, localUrls, onClose]);

  const onChangeUrls = useCallback((newUrls: string[]) => {
    setLocalUrls(newUrls);
  }, []);

  const onRemoveRegistriesHandler = useCallback(() => {
    scrollElementIntoViewById('submodule-registry', 'center', 0);
    dispatch({
      type: UPDATE_GIFT_OPTIONS,
      payload: {
        // biome-ignore-start lint/style/useNamingConvention: API expects the property name in snake_case
        gift_registry_urls: [],
        gift_registry_name: '',
        gift_registry_note: '',
        // biome-ignore-end lint/style/useNamingConvention: API expects the property name in snake_case
      },
    });
    // @ts-expect-error function not typed
    dispatch(saveEventThunk());
    onClose();
  }, [dispatch, onClose]);

  const openConfirmation = useCallback(() => {
    setShowConfirmation(true);
  }, []);

  const closeConfirmation = useCallback(() => {
    setShowConfirmation(false);
  }, []);

  const handleConfirmation = useCallback(() => {
    setShowConfirmation(false);
    onRemoveRegistriesHandler();
  }, [onRemoveRegistriesHandler]);

  const onChangeRegistryName = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      window.dataLayer.push({
        event: 'registry_note_title_added',
      });
      dispatch({
        type: UPDATE_GIFT_OPTIONS,
        // biome-ignore lint/style/useNamingConvention: API expects the property name in snake_case
        payload: {gift_registry_name: e.target.value},
      });
    },
    [dispatch]
  );

  const onChangeRegistryNote = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      dispatch({
        type: UPDATE_GIFT_OPTIONS,
        // biome-ignore lint/style/useNamingConvention: API expects the property name in snake_case
        payload: {gift_registry_note: e.target.value},
      });
    },
    [dispatch]
  );

  return (
    <OverlayContentWrapper
      contentClassName="registries__wrapper"
      footer={
        <RegistryFooter
          onRemove={openConfirmation}
          onSave={onSaveRegistriesHandler}
          localUrls={localUrls}
        />
      }
    >
      <VendorImages />

      <RegistryList onChangeUrls={onChangeUrls} urls={localUrls} />

      <Input
        className="gift-registry__name"
        data-qa-id="registry-name"
        type="text"
        label="name of recipient"
        value={giftRegistryName}
        onChange={onChangeRegistryName}
        placeholder="Share who the gifts are for"
      />

      <Input
        label="Note"
        className="gift-registry_note"
        data-qa-id="registry-note"
        type="textarea"
        value={giftRegistryNote}
        onChange={onChangeRegistryNote}
        placeholder="Example: Your presence is a present! But if you want, here are some gift ideas..."
        maxLength={150}
      />

      <ResponsiveModal
        isOpen={showConfirmation}
        onClose={closeConfirmation}
        title="Are you sure you want to remove this gift option?"
        modalId="gift-option-modal"
        primaryText="Yes, remove it"
        secondaryText="No, keep my gift"
        primaryAction={handleConfirmation}
        secondaryAction={closeConfirmation}
        portalMode
      />
    </OverlayContentWrapper>
  );
};
